import { Button } from "components/Button";
import { FormField } from "components/FormField";
import { useNotify } from "hooks/useToast/useToast";
import { useFieldArray, useFormContext } from "react-hook-form";
import { TQuizForm } from "types/quiz";

export const QuizForm = () => {
  const form = useFormContext<TQuizForm>();
  const { errors } = form.formState;

  const isLoading = false;
  const { notify } = useNotify();
  const { fields: questionFields, append: appendQuestion } = useFieldArray({
    control: form.control,
    name: 'questions',
  });

  const addQuestion = () => {
    appendQuestion({
      question: '',
      answers: [
        { text: '', correct: false },
        { text: '', correct: false },
      ],
      reward: 0,
      order: questionFields.length + 1,
    });
  };

  const handleCorrectAnswerChange = (
    questionIndex: number,
    answerIndex: number
  ) => {
    const updatedQuestions = form
      .getValues('questions')
      .map((question, qIndex) => {
        if (qIndex === questionIndex) {
          question.answers = question.answers.map((answer, aIndex) => ({
            ...answer,
            correct: aIndex === answerIndex,
          }));
        }
        return question;
      });

    form.setValue('questions', updatedQuestions, { shouldDirty: true });
  };

  const handleDeleteAnswer = (questionIndex: number, answerIndex: number) => {
    const currentAnswers = form.getValues(
      `questions.${questionIndex}.answers`
    );

    if (currentAnswers.length <= 2) {
      notify({
        type: 'error',
        meassage: 'Question must contain at least 2 answers',
      });
      return;
    }

    const filtered = currentAnswers.filter((_, i) => i !== answerIndex);

    form.setValue(`questions.${questionIndex}.answers`, filtered, {
      shouldDirty: true,
    });
    const questions = form.getValues('questions');

    form.setValue('questions', questions, { shouldDirty: true });
  };

  const handleAddAnswer = async (questionIndex: number) => {
    const currentAnswers = form.getValues(
      `questions.${questionIndex}.answers`
    );

    if (currentAnswers.length >= 4) {
      notify({
        meassage: 'Quiz can contain maximum 4 answers',
        type: 'error',
      });
      return;
    }

    form.setValue(`questions.${questionIndex}.answers`, [
      ...currentAnswers,
      { text: '', correct: false },
    ]);
    const questions = form.getValues('questions');

    form.setValue('questions', questions);
  };

  const handleDeleteQuestion = (questionIndex: number) => {
    const currentQuestions = form.getValues(`questions`);

    if (currentQuestions.length === 1) {
      notify({
        meassage: 'Quiz must contain at least 1 question',
        type: 'error',
      });
      return;
    }

    const filtered = currentQuestions.filter((_, i) => i !== questionIndex);

    form.setValue(`questions`, filtered, {
      shouldDirty: true,
    });
  };

  return (
    <div>
      <div className="mb-4 flex flex-col gap-4">
        <FormField
          id="quiz.name"
          label="Name"
          placeholder="Enter quiz name"
          {...form.register('name', {
            required: 'Name is required',
          })}
        />
        <FormField
          id="quiz.description"
          label="Description"
          placeholder="Enter quiz description"
          {...form.register('description', {
            required: 'Description is required',
          })}
        />
      </div>
      <div>
        <h4 className="mb-4">Questions:</h4>
        {questionFields.map((questionField, questionIndex) => {
          return (
            <fieldset
              key={questionField.id}
              className="mb-[15px] flex flex-col grow"
              disabled={isLoading}
            >
              <div className="grid grid-cols-[80%,20%] gap-2 max-w-[calc(100%-8px)]">
                <FormField
                  disabled={isLoading}
                  id="quiz.question"
                  label={`Question ${questionIndex + 1}`}
                  placeholder="Enter your question"
                  {...form.register(
                    `questions.${questionIndex}.question`,
                    {
                      required: 'Question is required',
                    }
                  )}
                />

                <div>
                  <FormField
                    disabled={isLoading}
                    id="quiz.question.reward"
                    label="Reward"
                    type="number"
                    placeholder="Enter reward"
                    {...form.register(
                      `questions.${questionIndex}.reward`, {
                      required: 'Reward is required',
                      valueAsNumber: true,
                      validate: (value) => {
                        if (value < 1) {
                          return 'must be > 1';
                        }
                        return true;
                      }
                    }
                    )}
                  />
                  {errors.questions?.[questionIndex]?.reward && (
                    <span className="text-red-500 ml-auto">
                      {errors.questions?.[questionIndex]?.reward?.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-2 pl-4">
                {questionField.answers.map((answer, answerIndex) => (
                  <div
                    key={answerIndex}
                    className="flex flex-row items-center gap-2"
                  >
                    <input
                      disabled={isLoading}
                      type="text"
                      className="bg-[#202020] text-white outline-none"
                      color="black"
                      placeholder={`Answer ${answerIndex + 1}`}
                      {...form.register(
                        `questions.${questionIndex}.answers.${answerIndex}.text`,
                        {
                          required: 'Answer is required',
                        }
                      )}
                    />
                    <label className="flex flex-row items-center gap-1">
                      <input
                        disabled={isLoading}
                        type="radio"
                        name={`correct-answer-${questionIndex}`}
                        value={answerIndex}
                        checked={answer.correct}
                        onChange={() =>
                          handleCorrectAnswerChange(
                            questionIndex,
                            answerIndex
                          )
                        }
                      />
                      Correct
                    </label>

                    <button
                      disabled={isLoading}
                      type="button"
                      className="text-violet11 hover:bg-violet4 focus:shadow-violet7 right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
                      aria-label="Close"
                      onClick={() =>
                        handleDeleteAnswer(questionIndex, answerIndex)
                      }
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex flex-row gap-3 mt-2 ml-4">
                <Button
                  onClick={() => handleAddAnswer(questionIndex)}
                  className="flex self-center"
                >
                  +
                  Add Answer
                </Button>
                <Button
                  onClick={() => handleDeleteQuestion(questionIndex)}
                  className="flex self-center"
                >
                  x
                  Delete Question
                </Button>
              </div>
            </fieldset>
          );
        })}
      </div>

      <Button onClick={addQuestion}>
        +
        Add Question
      </Button>
    </div>
  );
};
